import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Eye, Send, CheckCircle, XCircle, RotateCcw, Trash2, Search, X, Pencil } from "lucide-react";
import {
  useJournalEntriesPaged,
  useJournalUserOptions,
  useJournalEntryById,
  useCreateJournalEntry,
  useUpdateJournalEntry,
  useSubmitJournalEntry,
  useApproveJournalEntry,
  useRejectJournalEntry,
  useReverseJournalEntry,
  usePostableAccounts,
  useCostCentres,
} from "../hooks/useAccounting";
import { useSearchCustomers } from "@/features/customers/hooks/useCustomer";
import { useSuppliers } from "@/features/inventory/hooks/useSuppliers";
import { useBankAccounts } from "../hooks/useAccounting";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { useCurrency } from "@/shared/hooks/useCurrency";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import type {
  
  JournalEntryDto,
  CreateJournalEntryDto,
  CreateJournalEntryLineDto,
} from "../services/accountingApi";

const STATUS_COLORS: Record<string, string> = {
  Draft: "bg-gray-100 text-gray-800",
  Submitted: "bg-blue-100 text-blue-800",
  Approved: "bg-yellow-100 text-yellow-800",
  Posted: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
  Reversed: "bg-purple-100 text-purple-800",
};

// Maps the status tab name to the JournalEntryStatus enum value the API expects.
const STATUS_NAME_TO_ENUM: Record<string, number> = {
  Draft: 1,
  Submitted: 2,
  Approved: 3,
  Posted: 4,
  Rejected: 5,
  Reversed: 6,
};

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-KE", { year: "numeric", month: "short", day: "numeric" });
}

type JournalLookupOption = {
  id: string;
  code: string;
  name: string;
  meta?: string;
};

function JournalLookupCombobox({
  value,
  onChange,
  options,
  placeholder,
  emptyText,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: JournalLookupOption[];
  placeholder: string;
  emptyText: string;
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!value) {
      setQuery("");
      return;
    }

    const selected = options.find((o) => o.id === value);
    if (selected) {
      setQuery(`${selected.code} - ${selected.name}`);
    }
  }, [value, options]);

  const q = query.toLowerCase().trim();
  const filtered = q
    ? options.filter((o) => `${o.code} ${o.name} ${o.meta ?? ""}`.toLowerCase().includes(q))
    : options;

  return (
    <Combobox
      value={value}
      onValueChange={(v) => {
        const id = (v as string | null) ?? null;
        onChange(id);
        if (!id) {
          setQuery("");
          return;
        }

        const selected = options.find((o) => o.id === id);
        setQuery(selected ? `${selected.code} - ${selected.name}` : "");
      }}
      inputValue={query}
    >
      <ComboboxInput
        placeholder={placeholder}
        className="w-full h-8 text-[13px] border border-border/60 bg-muted/20 hover:bg-muted/30"
        showClear={!!value}
        onChange={(e) => setQuery(e.target.value)}
      />
      <ComboboxContent className="max-h-72 border-border/60">
        <ComboboxList>
          {filtered.length === 0 && <ComboboxEmpty>{emptyText}</ComboboxEmpty>}
          {filtered.map((o) => (
            <ComboboxItem key={o.id} value={o.id}>
              <span className="inline-flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-muted-foreground shrink-0">{o.code}</span>
                <span className="truncate">{o.name}</span>
                {o.meta && <span className="text-xs text-muted-foreground truncate">({o.meta})</span>}
              </span>
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

// ── Customer async search combobox ───────────────────────────────────────────

function CustomerPartyCombobox({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!value) {
      setSelectedName(null);
      setQuery("");
    }
  }, [value]);

  const { data: results } = useSearchCustomers(debouncedQuery);

  return (
    <Combobox
      value={value}
      onValueChange={(v) => {
        const id = (v as string | null) ?? null;
        if (!id) {
          onChange(null);
          setSelectedName(null);
          setQuery("");
          return;
        }
        const found = (results ?? []).find((c) => c.id === id);
        const name = found?.customerName ?? "";
        setSelectedName(name);
        setQuery(name);
        onChange(id);
      }}
      inputValue={query}
    >
      <ComboboxInput
        placeholder="Search customer..."
        className="w-full h-7 text-[11px] border border-border/60 bg-muted/20 hover:bg-muted/30"
        showClear={!!value}
        onChange={(e) => {
          setQuery(e.target.value);
          if (!e.target.value) {
            onChange(null);
            setSelectedName(null);
          }
        }}
      />
      <ComboboxContent className="max-h-72 border-border/60">
        <ComboboxList>
          {!debouncedQuery && !selectedName && (
            <ComboboxEmpty className="text-[11px]">Type to search customers…</ComboboxEmpty>
          )}
          {debouncedQuery && (results ?? []).length === 0 && (
            <ComboboxEmpty className="text-[11px]">No customers found.</ComboboxEmpty>
          )}
          {(results ?? []).map((c) => (
            <ComboboxItem key={c.id} value={c.id}>
              <span className="truncate text-[12px]">{c.customerName}</span>
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

// ── Create JE Dialog ──────────────────────────────────────────────────────────

const EMPTY_LINES: CreateJournalEntryLineDto[] = [
  { accountId: "", debitAmount: 0, creditAmount: 0, costCentreId: null, partyType: 0, partyId: null, remarks: null },
  { accountId: "", debitAmount: 0, creditAmount: 0, costCentreId: null, partyType: 0, partyId: null, remarks: null },
];

function CreateJournalEntryDialog({
  open,
  onOpenChange,
  entry,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: JournalEntryDto;
}) {
  const isEdit = !!entry;
  const { data: accounts } = usePostableAccounts();
  const { data: costCentres } = useCostCentres();
  const { data: suppliers } = useSuppliers();
  const { data: usersData } = useJournalUserOptions(open);
  const { data: bankAccounts } = useBankAccounts();
  const createMutation = useCreateJournalEntry();
  const updateMutation = useUpdateJournalEntry();
  const notify = useNotification();
  const { format: formatCurrency } = useCurrency();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [form, setForm] = useState({
    postingDate: new Date().toISOString().split("T")[0],
    narration: "",
    referenceNumber: "",
  });

  const [lines, setLines] = useState<CreateJournalEntryLineDto[]>(EMPTY_LINES);

  // Seed the form when opening: pre-fill from the entry in edit mode, reset to blanks otherwise.
  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    if (entry) {
      setForm({
        postingDate: entry.postingDate.split("T")[0],
        narration: entry.narration ?? "",
        referenceNumber: entry.referenceNumber ?? "",
      });
      setLines(
        entry.lines.map((l) => ({
          accountId: l.accountId,
          debitAmount: l.debitAmount,
          creditAmount: l.creditAmount,
          costCentreId: l.costCentreId,
          partyType: l.partyType,
          partyId: l.partyId,
          remarks: l.remarks,
        })),
      );
    } else {
      setForm({ postingDate: new Date().toISOString().split("T")[0], narration: "", referenceNumber: "" });
      setLines(EMPTY_LINES);
    }
  }, [open, entry]);

  const totalDebit = lines.reduce((s, l) => s + (l.debitAmount || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.creditAmount || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const postableAccounts: JournalLookupOption[] = [...(accounts ?? [])]
    .filter((a) => !a.isGroup)
    .sort((a, b) => a.accountCode.localeCompare(b.accountCode))
    .map((a) => ({
      id: a.id,
      code: a.accountCode,
      name: a.accountName,
    }));

  const postableCostCentres: JournalLookupOption[] = [...(costCentres ?? [])]
    .filter((cc) => !cc.isGroup)
    .sort((a, b) => a.costCentreCode.localeCompare(b.costCentreCode))
    .map((cc) => ({
      id: cc.id,
      code: cc.costCentreCode,
      name: cc.costCentreName,
      meta: cc.companyName ?? undefined,
    }));

  const getPartyOptions = (partyType: number): JournalLookupOption[] => {
    if (partyType === 2) {
      // Supplier — filter active only (API returns all)
      return (suppliers ?? [])
        .filter((s) => s.isActive)
        .sort((a, b) => (a.supplierName || "").localeCompare(b.supplierName || ""))
        .map((s) => ({
          id: s.id,
          code: s.supplierCode || "",
          name: s.supplierName || "",
        }));
    } else if (partyType === 3) {
      // The journal lookup returns active users in name order.
      return (usersData ?? [])
        .map((u) => ({
          id: u.id,
          code: u.email || "",
          name: `${u.firstName || ""} ${u.otherNames || ""}`.trim() || u.userName || "",
        }));
    } else if (partyType === 4) {
      // Bank accounts — filter active only
      return (bankAccounts ?? [])
        .filter((b) => b.isActive)
        .sort((a, b) => (a.accountName || "").localeCompare(b.accountName || ""))
        .map((b) => ({
          id: b.id,
          code: b.bankName || "",
          name: b.accountName || "",
          meta: b.accountNumber,
        }));
    }
    return [];
  };

  const addLine = () => {
    setLines([...lines, { accountId: "", debitAmount: 0, creditAmount: 0, costCentreId: null, partyType: 0, partyId: null, remarks: null }]);
  };

  const removeLine = (idx: number) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((_, i) => i !== idx));
  };

  const updateLine = (idx: number, field: keyof CreateJournalEntryLineDto, value: string | number | null) => {
    setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    const dto: CreateJournalEntryDto = {
      postingDate: new Date(form.postingDate).toISOString(),
      narration: form.narration || null,
      referenceNumber: form.referenceNumber || null,
      lines,
    };
    const onError = (err: unknown) => {
      const fe = getFieldErrors(err);
      if (fe) setFieldErrors(fe);
      notify.error(getErrorMessage(err));
    };

    if (isEdit && entry) {
      updateMutation.mutate(
        { id: entry.id, dto },
        {
          onSuccess: () => {
            notify.success("Journal entry updated — reset to Draft. Re-submit it for approval.");
            onOpenChange(false);
          },
          onError,
        },
      );
      return;
    }

    createMutation.mutate(dto, {
      onSuccess: () => {
        notify.success("Journal entry created");
        onOpenChange(false);
        setForm({ postingDate: new Date().toISOString().split("T")[0], narration: "", referenceNumber: "" });
        setLines(EMPTY_LINES);
      },
      onError,
    });
  };

  const saving = isEdit ? updateMutation.isPending : createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!flex !w-[95vw] !max-w-[95vw] sm:!max-w-[95vw] h-[90vh] max-h-[90vh] !gap-0 overflow-hidden p-0">
        <DialogHeader className="px-6 py-4 border-b border-border/60">
          <DialogTitle className="text-lg">{isEdit ? `Edit Journal Entry ${entry?.entryNumber ?? ""}` : "New Journal Entry"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-1 min-h-0 flex-col px-6 pb-5 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm shrink-0">
            <Field>
              <FieldLabel htmlFor="postingDate" className="text-xs">Posting Date <span className="text-red-500">*</span></FieldLabel>
              <DatePicker id="postingDate" value={form.postingDate} onChange={(v) => setForm({ ...form, postingDate: v })} required />
            </Field>
            <Field>
              <FieldLabel htmlFor="referenceNumber" className="text-xs">Reference #</FieldLabel>
              <Input id="referenceNumber" className="h-8 text-[12px]" value={form.referenceNumber} onChange={(e) => setForm({ ...form, referenceNumber: e.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="narration" className="text-xs">Narration</FieldLabel>
              <Input id="narration" className="h-8 text-[12px]" value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} />
            </Field>
          </div>

          {fieldErrors.lines && <p className="text-red-500 text-sm mt-3 shrink-0">{fieldErrors.lines.join(", ")}</p>}

          <div className="mt-3 min-h-0 flex-1 rounded-md border border-border/70 shadow-[inset_0_0_0_1px_hsl(var(--border)/0.25)] text-[12px] overflow-auto">
          <Table className="table-fixed min-w-[980px] [&_th]:py-2 [&_th]:px-3 [&_td]:py-2 [&_td]:px-3">
              <TableHeader className="sticky top-0 z-10 bg-background">
                <TableRow className="bg-muted/30 border-b border-border/70 hover:bg-muted/30">
                  <TableHead className="w-[24%] border-r border-border/50 font-semibold uppercase tracking-wider text-[11px]">Account</TableHead>
                  <TableHead className="w-[18%] border-r border-border/50 font-semibold uppercase tracking-wider text-[11px]">Cost Centre</TableHead>
                  <TableHead className="w-[10%] border-r border-border/50 font-semibold uppercase tracking-wider text-[11px]">Debit</TableHead>
                  <TableHead className="w-[10%] border-r border-border/50 font-semibold uppercase tracking-wider text-[11px]">Credit</TableHead>
                  <TableHead className="w-[13%] border-r border-border/50 font-semibold uppercase tracking-wider text-[11px]">Party Type</TableHead>
                  <TableHead className="w-[21%] border-r border-border/50 font-semibold uppercase tracking-wider text-[11px]">Party</TableHead>
                  <TableHead className="w-6" />
                </TableRow>
              </TableHeader>
              <TableBody className="[&_tr:last-child]:border-b-0">
                {lines.map((line, idx) => (
                  <TableRow key={idx} className={`border-b border-border/70 hover:bg-muted/10 ${idx % 2 === 0 ? 'bg-muted/5' : ''}`}>
                    <TableCell className="border-r border-border/50">
                      <JournalLookupCombobox
                        value={line.accountId || null}
                        onChange={(v) => updateLine(idx, "accountId", v ?? "")}
                        options={postableAccounts}
                        placeholder="Search..."
                        emptyText="None"
                      />
                    </TableCell>
                    <TableCell className="border-r border-border/50">
                      <JournalLookupCombobox
                        value={line.costCentreId}
                        onChange={(v) => updateLine(idx, "costCentreId", v)}
                        options={postableCostCentres}
                        placeholder="Search..."
                        emptyText="None"
                      />
                    </TableCell>
                    <TableCell className="border-r border-border/50">
                      <Input type="number" className="h-8 text-[12px] border-border/60 bg-muted/10" min={0} step={0.01} value={line.debitAmount || ""}
                        onChange={(e) => updateLine(idx, "debitAmount", Number(e.target.value))} />
                    </TableCell>
                    <TableCell className="border-r border-border/50">
                      <Input type="number" className="h-8 text-[12px] border-border/60 bg-muted/10" min={0} step={0.01} value={line.creditAmount || ""}
                        onChange={(e) => updateLine(idx, "creditAmount", Number(e.target.value))} />
                    </TableCell>
                    <TableCell className="border-r border-border/50">
                      <select
                        value={line.partyType || 0}
                        onChange={(e) => {
                          const pt = Number(e.target.value);
                          updateLine(idx, "partyType", pt);
                          if (pt === 0) updateLine(idx, "partyId", null);
                        }}
                        className="h-8 text-[12px] border border-border/60 bg-muted/10 rounded px-2 w-full"
                      >
                        <option value={0}>—</option>
                        <option value={1}>Customer</option>
                        <option value={2}>Supplier</option>
                        <option value={3}>Employee</option>
                        <option value={4}>Bank</option>
                      </select>
                    </TableCell>
                    <TableCell className="border-r border-border/50">
                      {line.partyType === 1 ? (
                        <CustomerPartyCombobox
                          value={line.partyId}
                          onChange={(v) => updateLine(idx, "partyId", v)}
                        />
                      ) : line.partyType && line.partyType !== 0 ? (
                        <JournalLookupCombobox
                          value={line.partyId}
                          onChange={(v) => updateLine(idx, "partyId", v)}
                          options={getPartyOptions(line.partyType)}
                          placeholder="Search..."
                          emptyText="None"
                        />
                      ) : (
                        <div className="h-8 text-[12px] text-muted-foreground flex items-center">—</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeLine(idx)} disabled={lines.length <= 2}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="font-semibold bg-muted/40 border-t border-border/70 hover:bg-muted/40 text-[11px] sticky bottom-0 z-10">
                  <TableCell colSpan={2} className="text-right border-r border-border/50">Totals</TableCell>
                  <TableCell className="border-r border-border/50">{formatCurrency(totalDebit)}</TableCell>
                  <TableCell className="border-r border-border/50">{formatCurrency(totalCredit)}</TableCell>
                  <TableCell colSpan={3}>
                    {isBalanced ? (
                      <Badge className="bg-green-100 text-green-800 text-[10px]">Balanced</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 text-[10px]">Diff: {formatCurrency(totalDebit - totalCredit)}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex justify-between text-sm shrink-0 sticky bottom-0 bg-background pt-3 border-t border-border/60">
            <Button type="button" variant="outline" onClick={addLine} className="h-8 text-xs">
              <Plus className="h-3 w-3 mr-1" /> Add Line
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" type="button" onClick={() => onOpenChange(false)} className="h-8 text-xs">Cancel</Button>
              <Button type="submit" disabled={saving || !isBalanced} className="h-8 text-xs">
                {isEdit
                  ? (saving ? "Saving..." : "Save Changes")
                  : (saving ? "Creating..." : "Create Draft")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Detail Dialog ─────────────────────────────────────────────────────────────

const EDITABLE_STATUSES = ["Draft", "Submitted", "Rejected"];

function JournalEntryDetailDialog({
  entryId,
  open,
  onOpenChange,
  onEdit,
}: {
  entryId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (entry: JournalEntryDto) => void;
}) {
  const { data: entry, isLoading } = useJournalEntryById(entryId || undefined);
  const submitMutation = useSubmitJournalEntry();
  const approveMutation = useApproveJournalEntry();
  const rejectMutation = useRejectJournalEntry();
  const reverseMutation = useReverseJournalEntry();
  const notify = useNotification();
  const confirm = useConfirm();
  const { format: formatCurrency } = useCurrency();
  const [rejectReason, setRejectReason] = useState("");
  const [reverseReason, setReverseReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [showReverse, setShowReverse] = useState(false);

  const canEdit = !!entry && EDITABLE_STATUSES.includes(entry.statusName) && !entry.sourceDocumentType;

  const handleEdit = async () => {
    if (!entry) return;
    const ok = await confirm({
      title: "Edit this journal entry?",
      description:
        "You are about to edit this journal entry. Any changes made will be recorded in the audit trail. Do you wish to continue?",
      confirmLabel: "Continue",
    });
    if (ok) onEdit(entry);
  };

  const handleAction = (action: "submit" | "approve") => {
    if (!entry) return;
    const mutation = action === "submit" ? submitMutation : approveMutation;
    mutation.mutate(entry.id, {
      onSuccess: () => { notify.success(`Journal entry ${action}${action === "submit" ? "ted" : "d"}`); },
      onError: (err) => notify.error(getErrorMessage(err)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!flex !w-[95vw] !max-w-[95vw] sm:!max-w-[95vw] h-[90vh] max-h-[90vh] !gap-0 overflow-hidden p-0">
        <DialogHeader className="px-6 py-4 border-b border-border/60">
          <DialogTitle>Journal Entry {entry?.entryNumber || ""}</DialogTitle>
        </DialogHeader>
        {isLoading || !entry ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">Loading...</div>
        ) : (
          <div className="flex flex-1 min-h-0 flex-col px-6 pb-5 pt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm shrink-0">
              <div><span className="text-muted-foreground">Status</span><div><Badge className={STATUS_COLORS[entry.statusName] || ""}>{entry.statusName}</Badge></div></div>
              <div><span className="text-muted-foreground">Date</span><div>{formatDate(entry.postingDate)}</div></div>
              <div><span className="text-muted-foreground">Total Debit</span><div className="font-mono">{formatCurrency(entry.totalDebit)}</div></div>
              <div><span className="text-muted-foreground">Total Credit</span><div className="font-mono">{formatCurrency(entry.totalCredit)}</div></div>
            </div>
            {entry.narration && <div className="text-sm mt-3 shrink-0"><span className="text-muted-foreground">Narration: </span>{entry.narration}</div>}
            {entry.referenceNumber && <div className="text-sm mt-1 shrink-0"><span className="text-muted-foreground">Reference: </span>{entry.referenceNumber}</div>}
            {entry.rejectionReason && <div className="text-sm text-red-600 mt-1 shrink-0">Rejected: {entry.rejectionReason}</div>}
            {entry.reversalReason && <div className="text-sm text-purple-600 mt-1 shrink-0">Reversed: {entry.reversalReason}</div>}

            <div className="mt-3 min-h-0 flex-1 rounded-md border border-border/70 shadow-[inset_0_0_0_1px_hsl(var(--border)/0.25)] overflow-auto">
              <Table className="min-w-[980px] [&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2">
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Cost Centre</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead>Party</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entry.lines.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm">{l.accountCode} - {l.accountName}</TableCell>
                      <TableCell className="text-sm">{l.costCentreName || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{l.debitAmount > 0 ? formatCurrency(l.debitAmount) : "—"}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{l.creditAmount > 0 ? formatCurrency(l.creditAmount) : "—"}</TableCell>
                      <TableCell className="text-sm">
                        {l.partyType !== 0
                          ? <span className="inline-flex flex-col"><span className="text-[11px] text-muted-foreground">{l.partyTypeName}</span><span>{l.partyName || <span className="text-muted-foreground italic">ID only</span>}</span></span>
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{l.remarks || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Actions */}
            <div className="mt-4 flex gap-2 justify-end flex-wrap shrink-0 sticky bottom-0 bg-background pt-3 border-t border-border/60">
              {canEdit && (
                <PermissionGate permissions={["journal_entries.edit"]}>
                  <Button size="sm" variant="outline" onClick={handleEdit}>
                    <Pencil className="h-4 w-4 mr-1" /> Edit
                  </Button>
                </PermissionGate>
              )}
              {entry.statusName === "Draft" && (
                <PermissionGate permissions={["journal_entries.submit"]}>
                  <Button size="sm" onClick={() => handleAction("submit")} disabled={submitMutation.isPending}>
                    <Send className="h-4 w-4 mr-1" /> Submit
                  </Button>
                </PermissionGate>
              )}
              {entry.statusName === "Submitted" && (
                <>
                  <PermissionGate permissions={["journal_entries.approve"]}>
                    <Button size="sm" onClick={() => handleAction("approve")} disabled={approveMutation.isPending}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve
                    </Button>
                  </PermissionGate>
                  <PermissionGate permissions={["journal_entries.reject"]}>
                    <Button size="sm" variant="destructive" onClick={() => setShowReject(true)}>
                      <XCircle className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </PermissionGate>
                </>
              )}
              {entry.statusName === "Posted" && (
                <PermissionGate permissions={["journal_entries.reverse"]}>
                  <Button size="sm" variant="outline" onClick={() => setShowReverse(true)}>
                    <RotateCcw className="h-4 w-4 mr-1" /> Reverse
                  </Button>
                </PermissionGate>
              )}
            </div>

            {showReject && (
              <div className="flex gap-2 mt-3 shrink-0">
                <Input placeholder="Rejection reason..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                <Button size="sm" variant="destructive" disabled={!rejectReason || rejectMutation.isPending}
                  onClick={() => rejectMutation.mutate({ journalEntryId: entry.id, rejectionReason: rejectReason }, {
                    onSuccess: () => { notify.success("Rejected"); setShowReject(false); setRejectReason(""); },
                    onError: (err) => notify.error(getErrorMessage(err)),
                  })}>Confirm Reject</Button>
              </div>
            )}

            {showReverse && (
              <div className="flex gap-2 mt-3 shrink-0">
                <Input placeholder="Reversal reason..." value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
                <Button size="sm" variant="outline" disabled={!reverseReason || reverseMutation.isPending}
                  onClick={() => reverseMutation.mutate({ journalEntryId: entry.id, reversalReason: reverseReason, reversalDate: null }, {
                    onSuccess: (reversal) => {
                      notify.success(`Reversal ${reversal.entryNumber} submitted for approval. A different user must approve it.`);
                      setShowReverse(false); setReverseReason("");
                    },
                    onError: (err) => notify.error(getErrorMessage(err)),
                  })}>Confirm Reverse</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function JournalEntriesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [editEntry, setEditEntry] = useState<JournalEntryDto | null>(null);
  const { format: formatCurrency } = useCurrency();

  // ── Search / filter / pagination state ──────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput.trim(), 300);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [createdByUserId, setCreatedByUserId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Reset to first page whenever any filter changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, startDate, endDate, createdByUserId, pageSize]);

  const { data, isLoading, isFetching } = useJournalEntriesPaged({
    pageNumber: page,
    pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter === "all" ? undefined : STATUS_NAME_TO_ENUM[statusFilter],
    // Send date-only values. The API applies [start-of-day, next-day) boundaries in UTC,
    // avoiding browser timezone conversion and the fragile 23:59:59 end-of-day convention.
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    createdByUserId: createdByUserId ?? undefined,
  });

  const items = data?.items ?? [];

  // Created-By options (active users).
  const { data: usersData } = useJournalUserOptions();
  const createdByOptions: JournalLookupOption[] = (usersData ?? [])
    .map((u) => ({
      id: u.id,
      code: u.email || "",
      name: `${u.firstName || ""} ${u.otherNames || ""}`.trim() || u.userName || "",
    }));

  const hasActiveFilters = !!debouncedSearch || !!startDate || !!endDate || !!createdByUserId;
  const clearFilters = () => {
    setSearchInput("");
    setStartDate("");
    setEndDate("");
    setCreatedByUserId(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Journal Entries</h1>
          <p className="text-muted-foreground">Create and manage general ledger journal entries</p>
        </div>
        <PermissionGate permissions={["journal_entries.create"]}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" /> New Journal Entry
          </Button>
        </PermissionGate>
      </div>

      <Card>
        <CardHeader className="pb-3 space-y-3">
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="Draft">Draft</TabsTrigger>
              <TabsTrigger value="Submitted">Submitted</TabsTrigger>
              <TabsTrigger value="Posted">Posted</TabsTrigger>
              <TabsTrigger value="Rejected">Rejected</TabsTrigger>
              <TabsTrigger value="Reversed">Reversed</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* ── Filter bar ── */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search number, reference, narration, creator…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <Field className="w-40">
              <FieldLabel className="text-xs">From</FieldLabel>
              <DatePicker value={startDate} onChange={setStartDate} />
            </Field>
            <Field className="w-40">
              <FieldLabel className="text-xs">To</FieldLabel>
              <DatePicker value={endDate} onChange={setEndDate} />
            </Field>
            <Field className="w-56">
              <FieldLabel className="text-xs">Created By</FieldLabel>
              <JournalLookupCombobox
                value={createdByUserId}
                onChange={setCreatedByUserId}
                options={createdByOptions}
                placeholder="Any user"
                emptyText="No users"
              />
            </Field>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs" onClick={clearFilters}>
                <X className="h-3 w-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No journal entries found</div>
          ) : (
            <div className={isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Narration</TableHead>
                    <TableHead>Created By</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead>Lines</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((entry) => (
                    <TableRow key={entry.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailId(entry.id)}>
                      <TableCell className="font-mono text-sm">{entry.entryNumber}</TableCell>
                      <TableCell className="text-sm">{formatDate(entry.postingDate)}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{entry.narration || "—"}</TableCell>
                      <TableCell className="text-sm">{entry.createdByUserName || "—"}</TableCell>
                      <TableCell><Badge className={STATUS_COLORS[entry.statusName] || ""}>{entry.statusName}</Badge></TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(entry.totalDebit)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{formatCurrency(entry.totalCredit)}</TableCell>
                      <TableCell className="text-sm text-center">{entry.lineCount}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setDetailId(entry.id); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <PaginationBar
            currentPage={data?.currentPage ?? page}
            pageSize={pageSize}
            totalCount={data?.totalCount ?? 0}
            totalPages={data?.totalPages ?? 1}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>

      <CreateJournalEntryDialog open={createOpen} onOpenChange={setCreateOpen} />
      <JournalEntryDetailDialog
        entryId={detailId}
        open={!!detailId}
        onOpenChange={(o) => { if (!o) setDetailId(null); }}
        onEdit={(entry) => { setDetailId(null); setEditEntry(entry); }}
      />
      <CreateJournalEntryDialog
        open={!!editEntry}
        entry={editEntry ?? undefined}
        onOpenChange={(o) => { if (!o) setEditEntry(null); }}
      />
    </div>
  );
}
